import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TreeItem } from "@components_v2/tree-view";
import { ImageDriveOn, ImageFolderSmallOn } from "@mzc-cloudplex/icons";
import FolderTreeViewSkeleton from "./folder-tree-view-skeleton";
import theme from "@styles/theme";

// 드래그 호버 시 접힌 카테고리를 자동으로 펼치는 지연시간 (ms)
const DRAG_HOVER_EXPAND_DELAY = 300;

const CategoryTreeViewItem = ({
    resourceType,
    data,
    renderTreeNodeIcon,
    onToggleGroupFold,
    onClickTreeItem,
    renderTree,
    foldDisabled,
    pending,
    onDropTreeItem,
    selectedCategoryId,
}) => {
    const nodeId = useMemo(() => (data?.id ? String(data.id) : ""), [data?.id]);

    const getTreeDepth = useMemo(() => {
        if (!data) return 0;
        if (data.isRoot || data.isGroup) return 0;
        return data.code.split("_").length - 1;
    }, [data]);

    const [isDragOver, setIsDragOver] = useState(false);
    const dragHoverExpandTimerRef = useRef(null);

    // 카테고리 그룹/루트는 드롭 대상에서 제외 (실제 카테고리에만 attach 가능)
    const isDroppable = !!onDropTreeItem && !!data?.id && !data?.isGroup && !data?.isRoot;
    // 드롭은 불가하지만 펼침 대상은 됨 (그룹/루트도 hover-expand 허용)
    const isExpandable =
        !foldDisabled &&
        data?.hasOwnProperty &&
        data.hasOwnProperty("isFold") &&
        data.isFold === true &&
        data.children?.length > 0;
    const isHoverable = isDroppable || isExpandable;

    const cancelDragHoverExpand = useCallback(() => {
        if (dragHoverExpandTimerRef.current) {
            clearTimeout(dragHoverExpandTimerRef.current);
            dragHoverExpandTimerRef.current = null;
        }
    }, []);

    const scheduleDragHoverExpand = useCallback(() => {
        if (!isExpandable) return;
        if (dragHoverExpandTimerRef.current) return;
        dragHoverExpandTimerRef.current = setTimeout(() => {
            dragHoverExpandTimerRef.current = null;
            onToggleGroupFold(data);
        }, DRAG_HOVER_EXPAND_DELAY);
    }, [isExpandable, data, onToggleGroupFold]);

    useEffect(() => cancelDragHoverExpand, [cancelDragHoverExpand]);

    const handleDragOver = useCallback(
        (e) => {
            if (!isHoverable) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = isDroppable ? "move" : "none";
            if (isDroppable && !isDragOver) setIsDragOver(true);
            scheduleDragHoverExpand();
        },
        [isHoverable, isDroppable, isDragOver, scheduleDragHoverExpand],
    );

    const handleDragLeave = useCallback(
        (e) => {
            if (!isHoverable) return;
            e.preventDefault();
            e.stopPropagation();
            if (isDroppable) setIsDragOver(false);
            cancelDragHoverExpand();
        },
        [isHoverable, isDroppable, cancelDragHoverExpand],
    );

    const handleDrop = useCallback(
        (e) => {
            if (!isHoverable) return;
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            cancelDragHoverExpand();
            if (!isDroppable) return;
            if (data.id === selectedCategoryId) return;
            onDropTreeItem(data);
        },
        [isHoverable, isDroppable, data, selectedCategoryId, onDropTreeItem, cancelDragHoverExpand],
    );

    if (!data?.id) return null;

    return (
        <TreeItem
            type={"Category"}
            depth={getTreeDepth}
            icon={renderTreeNodeIcon}
            nodeId={nodeId}
            label={data.name === "Uncategorized Assets" ? `Uncategorized ${resourceType}` : data.name}
            foldDisabled={foldDisabled}
            isOverCurrent={isDragOver}
            onDragOver={isHoverable ? handleDragOver : undefined}
            onDragLeave={isHoverable ? handleDragLeave : undefined}
            onDrop={isHoverable ? handleDrop : undefined}
            onClick={(e) => {
                e.stopPropagation();
                onClickTreeItem(data);
            }}
            onClickExpand={() => {
                // 설명: 열기/닫기 아이콘 전용
                data.hasOwnProperty("isFold") && onToggleGroupFold(data);
            }}
            sx={{
                borderTop: data.name === "Uncategorized Assets" ? `1px solid ${theme.palette.secondary[100]}` : `0`,
                borderBottom: data.name === "Uncategorized Assets" ? `1px solid  ${theme.palette.secondary[100]}` : `0`,
                marginTop: data.name === "Uncategorized Assets" ? 1.5 : 0,
                padding: data.name === "Uncategorized Assets" ? `12px 0` : 0,
                ".MuiTypography-root": { fontSize: "14px", fontWeight: "normal" },
            }}
        >
            {data.children?.length > 0 && renderTree(data?.children)}
            {pending && <FolderTreeViewSkeleton type={"child"} depth={getTreeDepth + 1} />}
        </TreeItem>
    );
};

export default CategoryTreeViewItem;
